import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Maximize2 } from 'lucide-react';
import ChartExpandModal from './ChartExpandModal';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const COLORS = ['#10b981','#3b82f6','#f59e0b','#8b5cf6','#06b6d4','#ec4899','#84cc16','#f97316','#6366f1','#ef4444'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm text-sm">
      <p className="font-medium text-slate-700">{payload[0].payload.name}</p>
      <p className="text-emerald-600">${payload[0].value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
    </div>
  );
};

const CustomBarLabel = ({ x, y, width, height, value }) => {
  if (value == null) return null;
  const label = `$${value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`;
  return (
    <text x={x + width + 5} y={y + height / 2} dy={4} fontSize={10} fill="#64748b">{label}</text>
  );
};

export default function IncomeByCategoryChart({ transactions }) {
  const [expanded, setExpanded] = useState(false);

  const allData = useMemo(() => {
    const grouped = {};
    transactions
      .filter(t => t.type === 'credit' && t.category)
      .forEach(t => { grouped[t.category] = (grouped[t.category] || 0) + (t.amount || 0); });
    return Object.entries(grouped)
      .map(([name, amount]) => ({ name, amount: parseFloat(amount.toFixed(2)) }))
      .sort((a, b) => b.amount - a.amount);
  }, [transactions]);

  const data = allData.slice(0, 10);

  return (
    <>
      <Card className="border-0 shadow-sm bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Income by Category</CardTitle>
          <button onClick={() => setExpanded(true)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </CardHeader>
        <CardContent>
          {data.length === 0 ? (
            <div className="flex items-center justify-center h-56 text-slate-400 text-sm">No income data for this period</div>
          ) : (
            <ResponsiveContainer width="100%" height={data.length * 30 + 30}>
              <BarChart data={data} layout="vertical" margin={{ left: 0, right: 80, top: 4, bottom: 4 }}>
                <XAxis
                  type="number"
                  tickFormatter={v => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
                  fontSize={11} axisLine={false} tickLine={false}
                />
                <YAxis type="category" dataKey="name" fontSize={11} width={130} axisLine={false} tickLine={false} interval={0} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" radius={[0, 4, 4, 0]} maxBarSize={22} label={<CustomBarLabel />}>
                  {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
      <ChartExpandModal
        open={expanded}
        onClose={() => setExpanded(false)}
        title="Income by Category"
        data={allData}
        chartType="bar"
        color="#10b981"
      />
    </>
  );
}